import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    useMediaQuery, useTheme, TextField, MenuItem, InputAdornment,
    Button, Dialog, DialogContent, DialogActions, Chip, CircularProgress,
    Collapse, Tooltip, TablePagination, FormControlLabel, Switch, Menu, ListItemIcon, ListItemText
} from '@mui/material';

import { toastSuccess, toastError, confirmAcao } from '../services/alerts';

import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import HistoryIcon from '@mui/icons-material/History';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import UndoIcon from '@mui/icons-material/Undo';

import api from '../services/api';
import { novaChaveOperacao, respostaConclusiva } from '../services/idempotencia';
import EstornoMovimentacaoDialog from './financeiro/EstornoMovimentacaoDialog';

const MESES = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const FORMAS_PAGAMENTO = ['PIX','DINHEIRO','CARTAO_CREDITO','CARTAO_DEBITO','BOLETO','TRANSFERENCIA'];

const CATEGORIA_LABEL = {
    ALUGUEL: 'Aluguel',
    MATERIAL: 'Material',
    ASSINATURA_SOFTWARE: 'Software',
    MARKETING: 'Marketing',
    IMPOSTOS: 'Impostos',
    CURSO_FORMACAO: 'Curso / Formação',
    OUTRO: 'Outro',
};

/**
 * @param filtroInicial filtro vindo de um indicador do painel financeiro
 *   (status, vencidas, emAberto, mes/ano). Aplicado quando muda, para que o
 *   numero clicado e a lista mostrem o mesmo recorte.
 */
export default function ContasPagar({ filtroInicial = null }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const hoje = new Date();
    const [mesNav, setMesNav] = useState({ mes: hoje.getMonth() + 1, ano: hoje.getFullYear() });

    const [contas, setContas] = useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [resumo, setResumo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pagamentoDialog, setPagamentoDialog] = useState({ open: false, conta: null });
    const [detalhesDialog, setDetalhesDialog] = useState({ open: false, conta: null });
    const [menuAnchor, setMenuAnchor] = useState({ el: null, conta: null });

    const abrirMenu = (e, conta) => setMenuAnchor({ el: e.currentTarget, conta });
    const fecharMenu = () => setMenuAnchor(prev => ({ ...prev, el: null }));
    const [pendingAction, setPendingAction] = useState(null);
    const runAfterMenuClose = (fn) => { setPendingAction(() => fn); fecharMenu(); };
    // Chave de idempotencia da baixa em andamento (uma por operacao).
    const chaveBaixaRef = useRef(null);
    const [estornoDialog, setEstornoDialog] = useState({ open: false, conta: null });
    const [pagamentoForm, setPagamentoForm] = useState({ dataPagamento: '', valor: '' });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);

    const [showFilters, setShowFilters] = useState(false);
    const [descricaoFilter, setDescricaoFilter] = useState('');
    const [debouncedDescricao, setDebouncedDescricao] = useState('');
    const [valorMin, setValorMin] = useState('');
    const [valorMax, setValorMax] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [categoriaFilter, setCategoriaFilter] = useState('');
    const [dataVencInicio, setDataVencInicio] = useState('');
    const [dataVencFim, setDataVencFim] = useState('');
    const [dataPagInicio, setDataPagInicio] = useState('');
    const [dataPagFim, setDataPagFim] = useState('');
    const [formaPagamento, setFormaPagamento] = useState('');
    const [contaBancariaId, setContaBancariaId] = useState('');
    const [planoPagamentoId, setPlanoPagamentoId] = useState('');
    const [psicologoId, setPsicologoId] = useState('');
    const [recorrente, setRecorrente] = useState('');
    const [vencidas, setVencidas] = useState(false);
    const [vencendoEm, setVencendoEm] = useState('');

    // Aplica o recorte pedido pelo painel (indicador clicavel).
    useEffect(() => {
        if (!filtroInicial) return;
        setStatusFilter(filtroInicial.status || '');
        setVencidas(!!filtroInicial.vencidas);
        setVencendoEm('');
        if (filtroInicial.mes && filtroInicial.ano) {
            setMesNav({ mes: filtroInicial.mes, ano: filtroInicial.ano });
        }
        setPage(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filtroInicial]);

    const [contasBancarias, setContasBancarias] = useState([]);
    const [planos, setPlanos] = useState([]);
    const [psicologos, setPsicologos] = useState([]);

    useEffect(() => {
        const t = setTimeout(() => setDebouncedDescricao(descricaoFilter), 300);
        return () => clearTimeout(t);
    }, [descricaoFilter]);

    useEffect(() => {
        api.get('/contas-bancarias').then(r => setContasBancarias(Array.isArray(r.data) ? r.data : (r.data?.content ?? []))).catch(() => {});
        api.get('/planos-pagamento').then(r => setPlanos(Array.isArray(r.data) ? r.data : (r.data?.content ?? []))).catch(() => {});
        api.get('/psicologos').then(r => setPsicologos(Array.isArray(r.data) ? r.data : (r.data?.content ?? []))).catch(() => {});
    }, []);

    const buildParams = useCallback(() => {
        const usaRangeData = !!(dataVencInicio || dataVencFim || dataPagInicio || dataPagFim || vencendoEm);
        const params = {};
        if (!usaRangeData) {
            params.mes = `${mesNav.ano}-${String(mesNav.mes).padStart(2, '0')}`;
        }
        if (debouncedDescricao) params.descricao = debouncedDescricao;
        if (statusFilter) params.status = statusFilter;
        if (categoriaFilter) params.categoriaDespesa = categoriaFilter;
        if (valorMin) params.valorMin = valorMin;
        if (valorMax) params.valorMax = valorMax;
        if (dataVencInicio) params.dataVencimentoInicio = dataVencInicio;
        if (dataVencFim) params.dataVencimentoFim = dataVencFim;
        if (dataPagInicio) params.dataPagamentoInicio = dataPagInicio;
        if (dataPagFim) params.dataPagamentoFim = dataPagFim;
        if (formaPagamento) params.formaPagamento = formaPagamento;
        if (contaBancariaId) params.contaBancariaId = contaBancariaId;
        if (planoPagamentoId) params.planoPagamentoId = planoPagamentoId;
        if (psicologoId) params.psicologoId = psicologoId;
        if (recorrente !== '') params.recorrente = recorrente;
        if (vencidas) params.vencidas = true;
        if (vencendoEm) params.vencendoEm = vencendoEm;
        return params;
    }, [mesNav, debouncedDescricao, statusFilter, categoriaFilter, valorMin, valorMax,
        dataVencInicio, dataVencFim, dataPagInicio, dataPagFim,
        formaPagamento, contaBancariaId, planoPagamentoId, psicologoId,
        recorrente, vencidas, vencendoEm]);

    const carregarContas = useCallback(async () => {
        setLoading(true);
        try {
            const params = { ...buildParams(), page, size: rowsPerPage, sort: 'dataVencimento,desc' };
            const [pageResp, resumoResp] = await Promise.all([
                api.get('/contas-pagar/paginado', { params }),
                api.get('/contas-pagar/resumo', { params: buildParams() })
            ]);
            setContas(pageResp.data?.content ?? []);
            setTotalElements(pageResp.data?.totalElements ?? 0);
            setResumo(resumoResp.data);
        } catch {
            /* erro exibido pelo handler global */
        } finally {
            setLoading(false);
        }
    }, [buildParams, page, rowsPerPage]);

    useEffect(() => { carregarContas(); }, [carregarContas]);

    useEffect(() => { setPage(0); }, [
        mesNav, debouncedDescricao, statusFilter, categoriaFilter, valorMin, valorMax,
        dataVencInicio, dataVencFim, dataPagInicio, dataPagFim,
        formaPagamento, contaBancariaId, planoPagamentoId, psicologoId,
        recorrente, vencidas, vencendoEm
    ]);

    // O estorno passa pelo extrato: o usuario ve valor, data e conta de cada
    // baixa ativa e escolhe qual corrigir — inclusive em titulo PARCIAL, que
    // antes nao oferecia a acao. Motivo obrigatorio, conferido no dialog e na API.
    const abrirEstornoDialog = (conta) => {
        setEstornoDialog({ open: true, conta });
    };

    const handleCancelar = async (id) => {
        const ok = await confirmAcao({
            title: 'Cancelar conta',
            text: 'Cancelar esta conta a pagar? Titulos com pagamento registrado precisam ter a baixa estornada antes.',
            confirmButtonText: 'Cancelar conta',
            cancelButtonText: 'Voltar',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;
        try {
            await api.patch(`/contas-pagar/${id}/cancelar`);
            toastSuccess('Conta cancelada com sucesso!');
            carregarContas();
        } catch (err) {
            console.error(err);
        }
    };

    const saldoEmAberto = (conta) => {
        if (!conta) return 0;
        if (conta.saldoEmAberto != null) return Number(conta.saldoEmAberto);
        return Number(conta.valor || 0) - Number(conta.valorPago || 0);
    };

    const abrirPagamentoDialog = (conta) => {
        setPagamentoDialog({ open: true, conta });
        // Operacao nova = chave nova (ver services/idempotencia.js).
        chaveBaixaRef.current = novaChaveOperacao('cp');
        setPagamentoForm({
            dataPagamento: new Date().toISOString().split('T')[0],
            // Sugere quitar o saldo; reduzir o valor gera baixa parcial.
            valor: saldoEmAberto(conta).toFixed(2),
        });
    };

    const handleRegistrarPagamento = async () => {
        try {
            const id = pagamentoDialog.conta.id_c_pagar || pagamentoDialog.conta.id;
            const valor = pagamentoForm.valor === '' ? null : Number(pagamentoForm.valor);
            const saldo = saldoEmAberto(pagamentoDialog.conta);
            if (valor != null && (Number.isNaN(valor) || valor <= 0)) {
                toastError('Informe um valor de pagamento maior que zero.');
                return;
            }
            if (valor != null && valor > saldo + 0.001) {
                toastError(`Valor maior que o saldo em aberto (R$ ${saldo.toFixed(2)}).`);
                return;
            }
            await api.patch(`/contas-pagar/${id}/pagar`, {
                dataPagamento: pagamentoForm.dataPagamento || null,
                valor,
                chaveIdempotencia: chaveBaixaRef.current,
            });
            toastSuccess(valor != null && valor < saldo - 0.001
                ? 'Pagamento parcial registrado!'
                : 'Pagamento registrado com sucesso!');
            chaveBaixaRef.current = null;
            setPagamentoDialog({ open: false, conta: null });
            carregarContas();
        } catch (error) {
            // Resposta do servidor = nada gravado: proxima tentativa e operacao
            // nova. Falha de rede preserva a chave.
            if (respostaConclusiva(error)) {
                chaveBaixaRef.current = novaChaveOperacao('cp');
            }
            console.error(error);
        }
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'PAGO': return <Chip label="Pago" color="success" size="small" sx={{ fontWeight: 'bold' }} />;
            case 'PENDENTE': return <Chip label="Pendente" color="warning" size="small" sx={{ fontWeight: 'bold' }} />;
            case 'VENCIDO': return <Chip label="Vencido" color="error" size="small" sx={{ fontWeight: 'bold' }} />;
            case 'PARCIAL': return <Chip label="Parcial" color="info" size="small" sx={{ fontWeight: 'bold' }} />;
            case 'CANCELADO': return <Chip label="Cancelado" color="default" size="small" sx={{ fontWeight: 'bold' }} />;
            default: return <Chip label={status || 'N/A'} size="small" />;
        }
    };

    const mesAnterior = () => setMesNav(prev =>
        prev.mes === 1 ? { mes: 12, ano: prev.ano - 1 } : { mes: prev.mes - 1, ano: prev.ano }
    );
    const mesSeguinte = () => setMesNav(prev =>
        prev.mes === 12 ? { mes: 1, ano: prev.ano + 1 } : { mes: prev.mes + 1, ano: prev.ano }
    );
    const irParaHoje = () => setMesNav({ mes: hoje.getMonth() + 1, ano: hoje.getFullYear() });
    const eMesAtual = mesNav.mes === hoje.getMonth() + 1 && mesNav.ano === hoje.getFullYear();

    const dateRangeAtivo = useMemo(() => (
        !!(dataVencInicio || dataVencFim || dataPagInicio || dataPagFim || vencendoEm)
    ), [dataVencInicio, dataVencFim, dataPagInicio, dataPagFim, vencendoEm]);

    const hasActiveFilters = useMemo(() => (
        !!statusFilter || !!categoriaFilter || !!valorMin || !!valorMax || !!dataVencInicio || !!dataVencFim ||
        !!dataPagInicio || !!dataPagFim || !!formaPagamento || !!contaBancariaId ||
        !!planoPagamentoId || !!psicologoId || recorrente !== '' || vencidas || !!vencendoEm
    ), [statusFilter, categoriaFilter, valorMin, valorMax, dataVencInicio, dataVencFim, dataPagInicio,
        dataPagFim, formaPagamento, contaBancariaId, planoPagamentoId, psicologoId,
        recorrente, vencidas, vencendoEm]);

    const handleLimparFiltros = () => {
        setStatusFilter(''); setCategoriaFilter(''); setValorMin(''); setValorMax('');
        setDataVencInicio(''); setDataVencFim(''); setDataPagInicio(''); setDataPagFim('');
        setFormaPagamento(''); setContaBancariaId(''); setPlanoPagamentoId('');
        setPsicologoId(''); setRecorrente(''); setVencidas(false); setVencendoEm('');
    };

    const totalMes = Number(resumo?.totalGeral ?? 0);
    const totalPago = Number(resumo?.totalPago ?? 0);
    const totalPendente = Number(resumo?.totalPendente ?? 0);
    const totalVencido = Number(resumo?.totalVencido ?? 0);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant={isMobile ? 'h5' : 'h4'} component="div" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    Contas a Pagar
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="Histórico de alterações">
                        <IconButton onClick={() => navigate('/financeiro/historico?tab=1')}
                                    sx={{ border: '1px solid #e0e0e0', borderRadius: 2 }}>
                            <HistoryIcon />
                        </IconButton>
                    </Tooltip>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/contas-pagar/novo')} color="primary">
                        Nova Despesa
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ p: isMobile ? 2 : 3, bgcolor: '#fff', borderRadius: 2 }}>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <IconButton onClick={mesAnterior} size="small" disabled={dateRangeAtivo}><ChevronLeftIcon /></IconButton>
                        <Typography variant="h6" fontWeight={700} sx={{ minWidth: 180, textAlign: 'center', opacity: dateRangeAtivo ? 0.4 : 1 }}>
                            {dateRangeAtivo ? 'Período personalizado' : `${MESES[mesNav.mes - 1]} ${mesNav.ano}`}
                        </Typography>
                        <IconButton onClick={mesSeguinte} size="small" disabled={dateRangeAtivo}><ChevronRightIcon /></IconButton>
                        {!eMesAtual && !dateRangeAtivo && (
                            <Button size="small" onClick={irParaHoje} sx={{ ml: 1, color: '#1ABB9C', textTransform: 'none', fontWeight: 600 }}>
                                Mês atual
                            </Button>
                        )}
                    </Box>

                    {resumo && totalElements > 0 && (
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Typography variant="body2" color="text.secondary">
                                Total: <strong>R$ {totalMes.toFixed(2)}</strong>
                            </Typography>
                            {totalPago > 0 && (
                                <Typography variant="body2" sx={{ color: 'success.main' }}>
                                    Pago: <strong>R$ {totalPago.toFixed(2)}</strong>
                                </Typography>
                            )}
                            {totalPendente > 0 && (
                                <Typography variant="body2" sx={{ color: 'warning.main' }}>
                                    Pendente: <strong>R$ {totalPendente.toFixed(2)}</strong>
                                </Typography>
                            )}
                            {totalVencido > 0 && (
                                <Typography variant="body2" sx={{ color: 'error.main' }}>
                                    Vencido: <strong>R$ {totalVencido.toFixed(2)}</strong>
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                    <TextField
                        placeholder="Pesquisar por descrição..."
                        variant="outlined"
                        fullWidth
                        value={descricaoFilter}
                        onChange={(e) => setDescricaoFilter(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#999' }} /></InputAdornment>,
                            endAdornment: descricaoFilter && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setDescricaoFilter('')}><ClearIcon /></IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Tooltip title="Filtros avançados">
                        <Box sx={{ position: 'relative' }}>
                            <IconButton
                                onClick={() => setShowFilters(!showFilters)}
                                sx={{
                                    border: '1px solid',
                                    borderColor: showFilters || hasActiveFilters ? 'primary.main' : '#e0e0e0',
                                    borderRadius: 2, p: 1.2,
                                    bgcolor: showFilters || hasActiveFilters ? 'rgba(25,118,210,0.08)' : 'transparent',
                                    color: showFilters || hasActiveFilters ? 'primary.main' : 'text.secondary',
                                }}
                            >
                                {showFilters ? <FilterListOffIcon /> : <FilterListIcon />}
                            </IconButton>
                            {hasActiveFilters && !showFilters && (
                                <Box sx={{ position: 'absolute', top: 4, right: 4, width: 8, height: 8, bgcolor: 'error.main', borderRadius: '50%' }} />
                            )}
                        </Box>
                    </Tooltip>
                </Box>

                <Collapse in={showFilters}>
                    <Box sx={{
                        p: 2, mb: 2,
                        bgcolor: '#f8f9fa', borderRadius: 2,
                        border: '1px dashed #e2e8f0',
                    }}>
                        <Box sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: '1fr',
                                sm: 'repeat(2, 1fr)',
                                md: 'repeat(3, 1fr)',
                                lg: 'repeat(4, 1fr)',
                            },
                            gap: 2,
                        }}>
                            <TextField select fullWidth size="small" label="Status" value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                <MenuItem value="PENDENTE">Pendente</MenuItem>
                                <MenuItem value="PAGO">Pago</MenuItem>
                                <MenuItem value="VENCIDO">Vencido</MenuItem>
                                <MenuItem value="PARCIAL">Parcial</MenuItem>
                                <MenuItem value="CANCELADO">Cancelado</MenuItem>
                            </TextField>

                            <TextField select fullWidth size="small" label="Categoria" value={categoriaFilter}
                                onChange={(e) => setCategoriaFilter(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                {Object.entries(CATEGORIA_LABEL).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                            </TextField>

                            <TextField fullWidth size="small" label="Valor mín. (R$)" type="number"
                                value={valorMin} onChange={(e) => setValorMin(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />
                            <TextField fullWidth size="small" label="Valor máx. (R$)" type="number"
                                value={valorMax} onChange={(e) => setValorMax(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />

                            <TextField fullWidth size="small" label="Vencimento de" type="date" InputLabelProps={{ shrink: true }}
                                value={dataVencInicio} onChange={(e) => setDataVencInicio(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />
                            <TextField fullWidth size="small" label="Vencimento até" type="date" InputLabelProps={{ shrink: true }}
                                value={dataVencFim} onChange={(e) => setDataVencFim(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />
                            <TextField fullWidth size="small" label="Pagamento de" type="date" InputLabelProps={{ shrink: true }}
                                value={dataPagInicio} onChange={(e) => setDataPagInicio(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />
                            <TextField fullWidth size="small" label="Pagamento até" type="date" InputLabelProps={{ shrink: true }}
                                value={dataPagFim} onChange={(e) => setDataPagFim(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />

                            <TextField select fullWidth size="small" label="Forma pagamento" value={formaPagamento}
                                onChange={(e) => setFormaPagamento(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                {FORMAS_PAGAMENTO.map(fp => <MenuItem key={fp} value={fp}>{fp.replace('_',' ')}</MenuItem>)}
                            </TextField>

                            <TextField select fullWidth size="small" label="Conta bancária" value={contaBancariaId}
                                onChange={(e) => setContaBancariaId(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                {contasBancarias.map(cb => (
                                    <MenuItem key={cb.id || cb.id_c_bancaria} value={cb.id || cb.id_c_bancaria}>
                                        {cb.banco} - {cb.numeroConta}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField select fullWidth size="small" label="Plano pagamento" value={planoPagamentoId}
                                onChange={(e) => setPlanoPagamentoId(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                {planos.map(pl => (
                                    <MenuItem key={pl.id || pl.id_p_pagamento} value={pl.id || pl.id_p_pagamento}>
                                        {pl.nome}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField select fullWidth size="small" label="Psicólogo" value={psicologoId}
                                onChange={(e) => setPsicologoId(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todos</MenuItem>
                                {psicologos.map(p => (
                                    <MenuItem key={p.id || p.id_psicologo} value={p.id || p.id_psicologo}>
                                        {p.nomeCompleto}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField select fullWidth size="small" label="Recorrente" value={recorrente}
                                onChange={(e) => setRecorrente(e.target.value)} sx={{ bgcolor: '#fff' }}>
                                <MenuItem value="">Todas</MenuItem>
                                <MenuItem value="true">Sim</MenuItem>
                                <MenuItem value="false">Não</MenuItem>
                            </TextField>

                            <TextField fullWidth size="small" label="Vencendo em (dias)" type="number"
                                value={vencendoEm} onChange={(e) => setVencendoEm(e.target.value)}
                                sx={{ bgcolor: '#fff' }} />
                        </Box>

                        <Box sx={{
                            mt: 2, pt: 2, borderTop: '1px solid #e2e8f0',
                            display: 'flex', flexWrap: 'wrap', gap: 2,
                            alignItems: 'center', justifyContent: 'space-between'
                        }}>
                            <FormControlLabel
                                control={<Switch checked={vencidas} onChange={(e) => setVencidas(e.target.checked)} />}
                                label="Somente vencidas"
                            />
                            {hasActiveFilters && (
                                <Button variant="text" color="error" size="small" onClick={handleLimparFiltros} sx={{ fontWeight: 'bold' }}>
                                    Limpar filtros
                                </Button>
                            )}
                        </Box>
                    </Box>
                </Collapse>

                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 2 }}>
                    {totalElements} registro(s) {dateRangeAtivo ? 'no período filtrado' : `em ${MESES[mesNav.mes - 1]} ${mesNav.ano}`}
                    {(debouncedDescricao || hasActiveFilters) && ' • Filtros aplicados'}
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress sx={{ color: '#1ABB9C' }} />
                    </Box>
                ) : (
                    <TableContainer>
                        <Table size={isMobile ? 'small' : 'medium'}>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                    <TableCell><strong>Descrição</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Categoria</strong></TableCell>
                                    <TableCell><strong>Valor</strong></TableCell>
                                    <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}><strong>Vencimento</strong></TableCell>
                                    <TableCell><strong>Status</strong></TableCell>
                                    <TableCell align="right"><strong>Ações</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {contas.length > 0 ? contas.map((item) => (
                                    <TableRow key={item.id_c_pagar || item.id} sx={{ '&:hover': { backgroundColor: '#fcfcfc' } }}>
                                        <TableCell sx={{ fontWeight: 500 }}>
                                            {item.descricao}
                                            {isMobile && item.categoriaDespesa && (
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    {CATEGORIA_LABEL[item.categoriaDespesa] || item.categoriaDespesa}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell', color: 'text.secondary', fontSize: '0.85rem' }}>
                                            {CATEGORIA_LABEL[item.categoriaDespesa] || item.categoriaDespesa || '—'}
                                        </TableCell>
                                        <TableCell>R$ {Number(item.valor).toFixed(2)}</TableCell>
                                        <TableCell sx={{ display: isMobile ? 'none' : 'table-cell' }}>
                                            {item.dataVencimento ? new Date(item.dataVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                                        </TableCell>
                                        <TableCell>{getStatusChip(item.statusFinanceiro)}</TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Ações">
                                                <IconButton size="small" onClick={(e) => abrirMenu(e, item)}>
                                                    <MoreVertIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                            <Typography color="text.secondary">
                                                Nenhum lançamento em {MESES[mesNav.mes - 1]} {mesNav.ano}.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}

                <TablePagination
                    component="div"
                    count={totalElements}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    labelRowsPerPage="Linhas por página"
                    labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
                />
            </Paper>

            <Menu
                anchorEl={menuAnchor.el}
                open={Boolean(menuAnchor.el)}
                onClose={fecharMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                TransitionProps={{ onExited: () => {
                    setMenuAnchor({ el: null, conta: null });
                    if (pendingAction) { pendingAction(); setPendingAction(null); }
                } }}
            >
                <MenuItem onClick={() => { const c = menuAnchor.conta; runAfterMenuClose(() => setDetalhesDialog({ open: true, conta: c })); }}>
                    <ListItemIcon><InfoOutlinedIcon fontSize="small" sx={{ color: '#1ABB9C' }} /></ListItemIcon>
                    <ListItemText>Detalhes</ListItemText>
                </MenuItem>
                {menuAnchor.conta?.statusFinanceiro !== 'PAGO' && menuAnchor.conta?.statusFinanceiro !== 'CANCELADO' && (
                    <MenuItem onClick={() => { const c = menuAnchor.conta; runAfterMenuClose(() => abrirPagamentoDialog(c)); }}>
                        <ListItemIcon><PaymentIcon fontSize="small" color="success" /></ListItemIcon>
                        <ListItemText>Registrar pagamento</ListItemText>
                    </MenuItem>
                )}
                {menuAnchor.conta?.statusFinanceiro !== 'PAGO' && menuAnchor.conta?.statusFinanceiro !== 'CANCELADO' && (
                    <MenuItem onClick={() => { const c = menuAnchor.conta; runAfterMenuClose(() => navigate(`/contas-pagar/${c.id_c_pagar || c.id}`)); }}>
                        <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                        <ListItemText>Editar</ListItemText>
                    </MenuItem>
                )}
                {['PAGO', 'PARCIAL'].includes(menuAnchor.conta?.statusFinanceiro) && (
                    <MenuItem onClick={() => { const c = menuAnchor.conta; runAfterMenuClose(() => abrirEstornoDialog(c)); }}>
                        <ListItemIcon><UndoIcon fontSize="small" sx={{ color: '#ed6c02' }} /></ListItemIcon>
                        <ListItemText>Estornar baixa</ListItemText>
                    </MenuItem>
                )}
                {menuAnchor.conta?.statusFinanceiro !== 'CANCELADO' && (
                    <MenuItem onClick={() => { const c = menuAnchor.conta; runAfterMenuClose(() => handleCancelar(c.id_c_pagar || c.id)); }}>
                        <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
                        <ListItemText>Cancelar conta</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            <Dialog
                open={detalhesDialog.open}
                onClose={() => setDetalhesDialog({ open: false, conta: null })}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
            >
                <Box sx={{
                    bgcolor: '#1ABB9C', color: '#fff', px: 3, py: 2.5,
                    display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <ReceiptLongIcon />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Detalhes da Conta
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }} noWrap>
                            {detalhesDialog.conta?.descricao || '-'}
                        </Typography>
                    </Box>
                    {detalhesDialog.conta && getStatusChip(detalhesDialog.conta.statusFinanceiro)}
                </Box>

                <DialogContent sx={{ p: 3 }}>
                    {detalhesDialog.conta && (() => {
                        const c = detalhesDialog.conta;
                        const fmtData = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—';
                        const fmtValor = (v) => `R$ ${Number(v || 0).toFixed(2)}`;
                        const Field = ({ label, value, accent }) => (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                                    {label}
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: accent ? 700 : 500, color: accent || 'text.primary' }}>
                                    {value || '—'}
                                </Typography>
                            </Box>
                        );
                        return (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <Box sx={{
                                    p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e2e8f0',
                                    display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2
                                }}>
                                    <Field label="Valor" value={fmtValor(c.valor)} accent="#1ABB9C" />
                                    <Field label="Vencimento" value={fmtData(c.dataVencimento)} />
                                    <Field label="Data do Pagamento" value={fmtData(c.dataPagamento)} />
                                    <Field label="Pago" value={`R$ ${Number(c.valorPago || 0).toFixed(2)}`} />
                                    <Field label="Em aberto" value={`R$ ${saldoEmAberto(c).toFixed(2)}`} />
                                    <Field label="Categoria" value={CATEGORIA_LABEL[c.categoriaDespesa] || c.categoriaDespesa} />
                                </Box>

                                <Box sx={{
                                    p: 2, borderRadius: 2, border: '1px solid #e2e8f0',
                                    display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2
                                }}>
                                    <Field label="Plano de Pagamento" value={c.nomePlanoPagamento} />
                                    <Field label="Forma de Pagamento" value={c.formaPagamento?.replace('_', ' ')} />
                                    <Field label="Psicólogo" value={c.nomePsicologo} />
                                    <Field label="Recorrente" value={c.recorrente ? `Sim (dia ${c.diaRecorrencia || '—'})` : 'Não'} />
                                </Box>

                                {c.observacoes && (
                                    <Box sx={{ p: 2, borderRadius: 2, bgcolor: '#fffbea', border: '1px dashed #f0c674' }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                                            Observações
                                        </Typography>
                                        <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                            {c.observacoes}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        );
                    })()}
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
                    <Button
                        onClick={() => {
                            setDetalhesDialog({ open: false, conta: null });
                            navigate('/financeiro/historico?tab=1');
                        }}
                        startIcon={<HistoryIcon />}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Histórico
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button
                        onClick={() => setDetalhesDialog({ open: false, conta: null })}
                        color="inherit"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Fechar
                    </Button>
                    {detalhesDialog.conta?.statusFinanceiro !== 'PAGO' && detalhesDialog.conta?.statusFinanceiro !== 'CANCELADO' && (
                        <Button
                            onClick={() => {
                                const c = detalhesDialog.conta;
                                setDetalhesDialog({ open: false, conta: null });
                                navigate(`/contas-pagar/${c.id_c_pagar || c.id}`);
                            }}
                            variant="contained"
                            startIcon={<EditIcon />}
                            sx={{ textTransform: 'none', fontWeight: 700, bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#159a80' } }}
                        >
                            Editar
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <EstornoMovimentacaoDialog
                open={estornoDialog.open}
                onClose={() => setEstornoDialog({ open: false, conta: null })}
                recurso="contas-pagar"
                idTitulo={estornoDialog.conta?.id_c_pagar || estornoDialog.conta?.id}
                descricaoTitulo={estornoDialog.conta?.descricao}
                onEstornado={carregarContas}
            />

            <Dialog
                open={pagamentoDialog.open}
                onClose={() => setPagamentoDialog({ open: false, conta: null })}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
            >
                <Box sx={{
                    bgcolor: '#1ABB9C', color: '#fff', px: 3, py: 2.5,
                    display: 'flex', alignItems: 'center', gap: 1.5
                }}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '50%',
                        bgcolor: 'rgba(255,255,255,0.18)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <PaymentIcon />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                            Registrar Pagamento
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Confirme os dados para marcar como pago
                        </Typography>
                    </Box>
                </Box>

                <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box sx={{
                        p: 2, borderRadius: 2, bgcolor: '#f8f9fa', border: '1px solid #e2e8f0',
                        display: 'flex', flexDirection: 'column', gap: 0.5
                    }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Conta
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            {pagamentoDialog.conta?.descricao || '-'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">
                                Vencimento: {pagamentoDialog.conta?.dataVencimento
                                    ? new Date(pagamentoDialog.conta.dataVencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
                                    : '-'}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main' }}>
                                R$ {Number(pagamentoDialog.conta?.valor || 0).toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>

                    <TextField
                        label="Valor pago"
                        type="number"
                        fullWidth
                        required
                        size="small"
                        value={pagamentoForm.valor}
                        onChange={(e) => setPagamentoForm({ ...pagamentoForm, valor: e.target.value })}
                        inputProps={{ min: 0, step: '0.01', 'aria-label': 'Valor pago' }}
                        helperText={`Saldo em aberto: R$ ${saldoEmAberto(pagamentoDialog.conta).toFixed(2)} — informe menos para baixa parcial`}
                    />
                    <TextField
                        label="Data do Pagamento"
                        type="date"
                        fullWidth
                        required
                        size="small"
                        value={pagamentoForm.dataPagamento}
                        onChange={(e) => setPagamentoForm({ ...pagamentoForm, dataPagamento: e.target.value })}
                        InputLabelProps={{ shrink: true }}
                        helperText="Data em que o valor foi pago"
                    />

                    <Box sx={{
                        p: 1.5, borderRadius: 2, bgcolor: 'rgba(26,187,156,0.08)',
                        border: '1px dashed rgba(26,187,156,0.4)'
                    }}>
                        <Typography variant="caption" color="text.secondary">
                            Forma de pagamento e conta bancária serão herdadas do plano de pagamento vinculado.
                            O valor será debitado automaticamente da conta bancária do plano.
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, pt: 0 }}>
                    <Button
                        onClick={() => setPagamentoDialog({ open: false, conta: null })}
                        color="inherit"
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleRegistrarPagamento}
                        variant="contained"
                        disabled={!pagamentoForm.dataPagamento}
                        sx={{
                            textTransform: 'none', fontWeight: 700,
                            bgcolor: '#1ABB9C', '&:hover': { bgcolor: '#159a80' }
                        }}
                        startIcon={<PaymentIcon />}
                    >
                        Confirmar Pagamento
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
